import { StackMessage, StructStack } from "@features/builder/crdt/crdtStructs"
import {
  GraphDetails,
  SocketGroupDto,
  SocketNode,
  SocketNodeDto,
  SocketTableNode,
} from "@features/builder/types"
import { NodeType } from "@features/builder/graph/types"
import { Distribution } from "@custom-types/graphs"
import {
  continuousNodeDistributionProperties,
  discreteNodeDistributionProperties,
} from "@utils/graph"
import { DfCpd } from "@store/services/converter/types"

export const convertMessageToReadableString = (msg: StackMessage, stack?: StructStack) => {
  if (!stack) return msg.value
  switch (msg.action) {
    case "add":
      return convertAdd(msg)
    case "update":
      return convertUpdate(msg, stack)
    case "remove":
      return convertRemove(msg)
  }
}

const convertAdd = (msg: StackMessage) => {
  switch (msg.key) {
    case "id":
      return `id ${msg.value}`
    case "data": {
      const value: GraphDetails = JSON.parse(msg.value as string)
      return `Graph product ${value.Product} with version ${value.Version}`
    }
    case "groups": {
      const value: SocketGroupDto = JSON.parse(msg.value)
      return `Group '${value.data.meta.label}'`
    }
    case "nodes": {
      const value: SocketNodeDto = JSON.parse(msg.value)
      return convertAddNode(value)
    }
    case "version":
      return `Added version ${msg.value}`
  }
  return "stack"
}

const convertAddNode = (node: SocketNodeDto) => {
  return `Node '${node.data.meta.label}' type ${node.type}`
}

const convertUpdate = (msg: StackMessage, stack: StructStack) => {
  switch (msg.key) {
    case "id":
      return `id ${msg.value}`
    case "data": {
      const data: GraphDetails = JSON.parse(msg.value as string)
      return `Graph product ${data.Product} with version ${data.Version}`
    }
    case "groups":
      return convertUpdateGroup(JSON.parse(msg.value), JSON.parse(msg.next ?? "{}"))
    case "nodes":
      return convertUpdateNode(JSON.parse(msg.value), JSON.parse(msg.next ?? "{}"), stack)
    case "version":
      return `version ${msg.value}`
  }
  return "stack"
}

const convertRemove = (msg: StackMessage) => {
  switch (msg.key) {
    case "id":
      return `id ${msg.value}`
    case "data": {
      const data: GraphDetails = JSON.parse(msg.value as string)
      return `Graph product ${data.Product} with version ${data.Version}`
    }
    case "groups": {
      const value: SocketGroupDto = JSON.parse(msg.value)
      return `Group ${value.data.meta.label}`
    }
    case "nodes": {
      const value: SocketNodeDto = JSON.parse(msg.value)
      return `Node ${value.data.meta.label} type ${value.type}`
    }
    case "version":
      return `version ${msg.value}`
  }
  return "Removed stack"
}

const convertUpdateGroup = (prev: SocketGroupDto, next: SocketGroupDto): string => {
  let message = "Update group "
  if (prev.data.meta.label != next.data.meta.label) {
    message += `label ${prev.data.meta.label}->${next.data.meta.label} `
  }
  if (prev.position.x !== next.position.x || prev.position.y !== next.position.y) {
    message += `Position (${prev.position.x}, ${prev.position.y})->(${next.position.x}, ${next.position.y}) `
  }
  if (prev.style.width !== next.style.width || prev.style.height !== next.style.height) {
    message += `Size (${prev.style.width}, ${prev.style.height})->(${next.style.width}, ${next.style.height}) `
  }
  return message
}

const convertUpdateNode = (
  prev: SocketNodeDto,
  next: SocketNodeDto,
  stack: StructStack,
): string => {
  let message = convertMeta(prev, next, stack)

  // position
  if (prev.position.x !== next.position.x || prev.position.y !== next.position.y) {
    message += `Position (${prev.position.x}, ${prev.position.y})->(${next.position.x}, ${next.position.y}) `
  }
  // type changed so return at this point - can't compare two types
  if (next.type !== prev.type) {
    message += `Type ${prev.type}->${next.type} `
    return `Node ${prev.data.meta.label} ${message}`
  }
  // convert node specific messages
  if (
    prev.type === NodeType.Table ||
    prev.type === NodeType.ForecastTable ||
    prev.type === NodeType.ScenarioTable
  ) {
    message += convertTableNode(prev as SocketTableNode, next as SocketTableNode, stack)
  } else {
    message += convertUpdateNodeInternal(prev as SocketNode, next as SocketNode, stack)
    // check for edges change
    if (
      (prev as SocketNode).data.node.properties.parents_lst &&
      (next as SocketNode).data.node.properties.parents_lst
    ) {
      message += convertEdgesChange(
        prev as SocketNode,
        next as SocketNode,
        stack,
        (prev as SocketNode).data.node.properties.parents_lst ?? [],
        (next as SocketNode).data.node.properties.parents_lst ?? [],
      )
    }
  }

  if (message.length > 0) return `Node '${prev.data.meta.label}' ${message}`
  return `Node '${prev.data.meta.label}' ${prev.type} _->updated (generic message)`
}

const convertMeta = (prev: SocketNodeDto, next: SocketNodeDto, stack: StructStack) => {
  let out = ""
  const keys = (Object.keys(prev.data.meta) as (keyof SocketNodeDto["data"]["meta"])[]) ?? []
  keys.forEach((key) => {
    if (key === "groups") return
    if (prev.data.meta[key] !== next.data.meta[key]) {
      out += `${key} ${prev.data.meta[key]} -> ${next.data.meta[key]} `
    }
  })
  // groups
  if (!prev.data.meta.groups && next.data.meta.groups) {
    out += `Group _->${next.data.meta.groups.join(",")} `
  } else if (next.data.meta.groups && prev.data.meta.groups) {
    const removedGroups = prev.data.meta.groups!.filter((x) => !next.data.meta.groups!.includes(x))
    const addedGroups = next.data.meta.groups!.filter((x) => !prev.data.meta.groups!.includes(x))
    if (removedGroups.length > 0) out += `Group removed->${removedGroups.join(",")} `
    if (addedGroups.length > 0) out += `Group added>${addedGroups.join(",")} `
  }
  return out
}

const convertUpdateNodeInternal = (
  prev: SocketNode,
  next: SocketNode,
  stack: StructStack,
): string => {
  if (prev.data.node.properties.a_sample_value != next.data.node.properties.a_sample_value) {
    // Value changed so other information is not considered to be important
    return `Value ${prev.data.node.properties.a_sample_value}->${prev.data.node.properties.a_sample_value} `
  }
  switch (prev.type) {
    case NodeType.Categorical:
      return convertCategoricalNode(prev, next, stack)
    case NodeType.Continuous:
      return convertContinuousNode(prev, next, stack)
    case NodeType.Deterministic:
      return convertDeterministicNode(prev, next, stack)
    case NodeType.Discrete:
      return convertDiscreteNode(prev, next, stack)
    case NodeType.Forecast:
      return convertForecastNode(prev, next, stack)
    case NodeType.Lag:
      return convertLagNode(prev, next, stack)
    case NodeType.Mixture:
      return convertMixtureNode(prev, next, stack)
    case NodeType.Scenario:
      return convertScenarioNode(prev, next, stack)
    default:
      return ""
  }
}

const convertCategoricalNode = (prev: SocketNode, next: SocketNode, stack: StructStack): string => {
  let out = ""
  const visitedKeys: string[] = []
  Object.keys(prev.data.node.properties.df_cpd).forEach((key) => {
    visitedKeys.push(key)
    if (key === "distribution") return
    if (!next.data.node.properties.df_cpd[key]) {
      out += `Category remove->${key} ${prev.data.node.properties.df_cpd[key]} `
      return
    }
    if (prev.data.node.properties.df_cpd[key][0] !== next.data.node.properties.df_cpd[key][0]) {
      out += `Category ${key} changed ${prev.data.node.properties.df_cpd[key][0]}->${next.data.node.properties.df_cpd[key][0]} `
    }
  })
  Object.keys(next.data.node.properties.df_cpd).forEach((key) => {
    if (visitedKeys.includes(key)) return
    out += `Category add->${key} ${next.data.node.properties.df_cpd[key]} `
  })
  return out
}

const convertContinuousNode = (prev: SocketNode, next: SocketNode, stack: StructStack): string => {
  const prevDf = prev.data.node.properties.df_cpd
  const nextDf = next.data.node.properties.df_cpd
  if (prevDf.distribution?.[0] !== nextDf.distribution?.[0]) {
    return `Distribution ${prevDf.distribution}->${
      nextDf.distribution
    } ${getContinuousDistributionParams(nextDf.distribution?.[0] as Distribution, nextDf)} `
  } else {
    return compareContinuousDistributionParams(
      nextDf.distribution?.[0] as Distribution,
      prevDf,
      nextDf,
    )
  }
}

const compareContinuousDistributionParams = (
  distribution: Distribution,
  prevDf: DfCpd,
  nextDf: DfCpd,
) => {
  let out = ""
  continuousNodeDistributionProperties[distribution]?.forEach((prop) => {
    if (prevDf[prop] !== nextDf[prop]) out += `${prop} ${prevDf[prop]}->${nextDf[prop]} `
  })
  return out
}

const getContinuousDistributionParams = (distribution: Distribution, df: DfCpd) => {
  let out = ""
  continuousNodeDistributionProperties[distribution]?.forEach((prop) => {
    out += `${prop} _->${df[prop]} `
  })
  return out
}

const convertDeterministicNode = (
  prev: SocketNode,
  next: SocketNode,
  stack: StructStack,
): string => {
  if (
    prev.data.node.properties.df_cpd.expression?.[0] !==
    next.data.node.properties.df_cpd.expression?.[0]
  ) {
    return `Expression ${prev.data.node.properties.df_cpd.expression?.[0]}->${next.data.node.properties.df_cpd.expression?.[0]} `
  }
  return ""
}

const convertDiscreteNode = (prev: SocketNode, next: SocketNode, stack: StructStack): string => {
  const prevDf = prev.data.node.properties.df_cpd
  const nextDf = next.data.node.properties.df_cpd
  if (prevDf.distribution?.[0] !== nextDf.distribution?.[0]) {
    return `Distribution ${prevDf.distribution}->${
      nextDf.distribution
    } ${getDiscreteDistributionParams(nextDf.distribution?.[0] as Distribution, nextDf)}`
  } else {
    return compareDiscreteDistributionParams(
      nextDf.distribution?.[0] as Distribution,
      prevDf,
      nextDf,
    )
  }
}

const compareDiscreteDistributionParams = (
  distribution: Distribution,
  prevDf: DfCpd,
  nextDf: DfCpd,
) => {
  let out = ""
  discreteNodeDistributionProperties[distribution]?.forEach((prop) => {
    if (prevDf[prop] !== nextDf[prop]) out += `${prop} ${prevDf[prop]}->${nextDf[prop]}`
  })
  return out
}

const getDiscreteDistributionParams = (distribution: Distribution, df: DfCpd) => {
  let out = ""
  discreteNodeDistributionProperties[distribution]?.forEach((prop) => {
    if (df[prop]) out += `${prop} _->${df[prop]}`
  })
  return out
}

const convertForecastNode = (prev: SocketNode, next: SocketNode, stack: StructStack): string => {
  return ""
}

const convertLagNode = (prev: SocketNode, next: SocketNode, stack: StructStack): string => {
  return ""
}

const convertMixtureNode = (prev: SocketNode, next: SocketNode, stack: StructStack): string => {
  return ""
}

const convertTableNode = (
  prev: SocketTableNode,
  next: SocketTableNode,
  stack: StructStack,
): string => {
  return ""
}

const convertScenarioNode = (prev: SocketNode, next: SocketNode, stack: StructStack): string => {
  return ""
}

const convertEdgesChange = (
  prev: SocketNode,
  next: SocketNode,
  stack: StructStack,
  prevParent: string[],
  nextParent: string[],
) => {
  let out = ""
  if (prevParent.length < nextParent.length) {
    out += convertAddEdge(prev.data.meta.label, prevParent, nextParent, stack)
  } else if (prevParent.length > nextParent.length) {
    out += convertRemoveEdge(prev.data.meta.label, prevParent, nextParent, stack)
  }
  return out
}

const convertRemoveEdge = (
  nodeLabel: string,
  prevParent: string[],
  nextParent: string[],
  stack: StructStack,
): string => {
  const inter = prevParent.filter((x) => !nextParent.includes(x))
  const str = stack.getNode(inter[0] ?? "")
  if (!str) return ""
  const node = JSON.parse(str)
  return `Disconnected ${nodeLabel} from ${node.data.meta.label} `
}

const convertAddEdge = (
  nodeLabel: string,
  prevParent: string[],
  nextParent: string[],
  stack: StructStack,
): string => {
  const inter = nextParent.filter((x) => !prevParent.includes(x))
  const str = stack.getNode(inter[0] ?? "")
  if (!str) return ""
  const node = JSON.parse(str)
  return `Connected ${nodeLabel} to ${node.data.meta.label} `
}
